import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useParams, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import Loader from './Loader';
import './Product.css';
import { ADD_PRODUCT_TO_WISHLIST, GET_CUSTOMER_WISHLIST } from '../queries/customerQueries';
import { formatStockStatus, calculateBundleStockStatus } from '../utils/stockUtils';

const GET_PRODUCT_DETAILS = gql`
    query getProductDetails($urlKey: String!) {
        products(filter: {url_key: {eq: $urlKey}}) {
            items {
                id
                uid
                sku
                name
                __typename
                description {
                    html
                }
                media_gallery {
                    url
                    label
                }
                price_range {
                    minimum_price {
                        final_price {
                            currency
                            value
                        }
                        regular_price {
                            currency
                            value
                        }
                        discount {
                            amount_off
                            percent_off
                        }
                    }
                }
                stock_status
                only_x_left_in_stock
                categories {
                    id
                    name
                    url_path
                    url_suffix
                    breadcrumbs {
                        category_id
                        category_name
                        category_url_path
                    }
                }
                reviews {
                    items {
                        average_rating
                        ratings_breakdown {
                            name
                            value
                        }
                        nickname
                        summary
                        text
                        created_at
                    }
                }
                rating_summary
                review_count
                ... on ConfigurableProduct {
                    configurable_options {
                        id
                        attribute_id
                        label
                        position
                        use_default
                        attribute_code
                        values {
                            value_index
                            label
                            store_label
                            use_default_value
                            swatch_data {
                                value
                            }
                        }
                    }
                    variants {
                        product {
                            id
                            sku
                            name
                            stock_status
                            price_range {
                                minimum_price {
                                    final_price {
                                        currency
                                        value
                                    }
                                }
                            }
                        }
                        attributes {
                            code
                            value_index
                            label
                        }
                    }
                }
                ... on GroupedProduct {
                    items {
                        product {
                            id
                            sku
                            name
                            stock_status
                            price_range {
                                minimum_price {
                                    final_price {
                                        currency
                                        value
                                    }
                                    regular_price {
                                        currency
                                        value
                                    }
                                }
                            }
                            small_image {
                                url
                            }
                        }
                        qty
                    }
                }
                ... on BundleProduct {
                    items {
                        option_id
                        title
                        required
                        type
                        position
                        sku
                        options {
                            id
                            uid
                            quantity
                            position
                            is_default
                            price
                            price_type
                            can_change_quantity
                            product {
                                id
                                sku
                                name
                                stock_status
                                price_range {
                                    minimum_price {
                                        final_price {
                                            currency
                                            value
                                        }
                                    }
                                }
                                small_image {
                                    url
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

const GET_REVIEW_METADATA = gql`
    query getProductReviewRatingsMetadata {
        productReviewRatingsMetadata {
            items {
                id
                name
                values {
                    value_id
                    value
                }
            }
        }
    }
`;

const CREATE_PRODUCT_REVIEW = gql`
    mutation createProductReview($input: CreateProductReviewInput!) {
        createProductReview(input: $input) {
            review {
                nickname
                summary
                text
                average_rating
                ratings_breakdown {
                    name
                    value
                }
            }
        }
    }
`;

const Product = ({ urlKey: urlKeyProp }) => {
  const { urlKey: urlKeyParam } = useParams();
  const urlKey = urlKeyProp || urlKeyParam;
  const location = useLocation();
  const { addToCart, addBundleToCart, loading: cartLoading } = useCart();
  const { isAuthenticated, openLoginModal } = useAuth();
  const { updateBreadcrumbs } = useBreadcrumb();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [wishlistMessage, setWishlistMessage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('details');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewMessage, setReviewMessage] = useState('');
  const [reviewForm, setReviewForm] = useState({
    nickname: '',
    rating: 5,
    summary: '',
    text: ''
  });

  const { loading, error, data, refetch } = useQuery(GET_PRODUCT_DETAILS, {
    variables: { urlKey }
  });

  const { data: wishlistData } = useQuery(GET_CUSTOMER_WISHLIST, {
    skip: !isAuthenticated
  });

  const [addProductToWishlist, { loading: wishlistLoading }] = useMutation(ADD_PRODUCT_TO_WISHLIST);

  const { data: reviewMetadata } = useQuery(GET_REVIEW_METADATA);

  const [createReview, { loading: reviewLoading }] = useMutation(CREATE_PRODUCT_REVIEW);

  const product = data?.products?.items?.[0];
  const isConfigurable = product?.__typename === 'ConfigurableProduct';
  const isGrouped = product?.__typename === 'GroupedProduct';
  const isBundle = product?.__typename === 'BundleProduct';
  const reviews = product?.reviews?.items || [];
  const reviewCount = product?.review_count || 0;
  const ratingSummary = product?.rating_summary || 0;
  const ratingMetadata = reviewMetadata?.productReviewRatingsMetadata?.items || [];

  // Set breadcrumbs from product categories data
  useEffect(() => {
    if (product && product.name) {
      let crumbs = [{ label: 'Home', path: '/' }];

      // Try to get breadcrumbs from product categories
      if (product.categories && product.categories.length > 0) {
        // Find a category with breadcrumbs (prefer the one with the most breadcrumbs)
        const categoryWithBreadcrumbs = product.categories
          .filter(cat => cat.breadcrumbs && cat.breadcrumbs.length > 0)
          .sort((a, b) => (b.breadcrumbs?.length || 0) - (a.breadcrumbs?.length || 0))[0];

        if (categoryWithBreadcrumbs && categoryWithBreadcrumbs.breadcrumbs) {
          // Add breadcrumbs from the category
          categoryWithBreadcrumbs.breadcrumbs.forEach(crumb => {
            crumbs.push({
              label: crumb.category_name,
              path: `/${crumb.category_url_path}.html`
            });
          });

          // Add the current category
          crumbs.push({
            label: categoryWithBreadcrumbs.name,
            path: `/${categoryWithBreadcrumbs.url_path}${categoryWithBreadcrumbs.url_suffix || ''}`
          });
        }
      }

      // Add the product as the final breadcrumb
      crumbs.push({ label: product.name, path: location.pathname });

      updateBreadcrumbs(crumbs);
    }
  }, [product?.name, product?.categories, location.pathname, updateBreadcrumbs]);

  // Find matching variant based on selected options
  const findMatchingVariant = (options) => {
    if (!isConfigurable || !product?.variants) return null;

    return product.variants.find(variant => {
      return variant.attributes.every(attr =>
        options[attr.code] === attr.value_index
      );
    });
  };

  // Update selected variant when options change
  useEffect(() => {
    if (isConfigurable && product) {
      const variant = findMatchingVariant(selectedOptions);
      setSelectedVariant(variant);
    }
  }, [selectedOptions, isConfigurable, product]);

  if (loading) return <Loader fullScreen />;
  if (error) return <div>Error: {error.message}</div>;

  if (!product) {
    return <div>Product not found</div>;
  }

  // Get current price (from variant if selected, otherwise base product)
  const currentProduct = selectedVariant?.product || product;

  // Get effective stock status - use manual calculation for bundles
  const getEffectiveStockStatus = () => {
    if (isBundle) {
      return calculateBundleStockStatus(product);
    }
    return currentProduct.stock_status || product.stock_status;
  };

  const effectiveStockStatus = getEffectiveStockStatus();
  const price = currentProduct.price_range?.minimum_price?.final_price;
  const regularPrice = currentProduct.price_range?.minimum_price?.regular_price;
  const discount = currentProduct.price_range?.minimum_price?.discount;

  const handleOptionChange = (attributeCode, valueIndex) => {
    setSelectedOptions(prev => ({
      ...prev,
      [attributeCode]: parseInt(valueIndex)
    }));
  };

  const canAddToCart = () => {
    // For simple products, can add if in stock
    if (!isConfigurable && !isGrouped && !isBundle) {
      return effectiveStockStatus === 'IN_STOCK';
    }

    // For grouped products, check if any products are selected with qty > 0
    if (isGrouped) {
      const hasSelectedProducts = selectedOptions && Object.values(selectedOptions).some(qty => qty > 0);
      return hasSelectedProducts;
    }

    // For bundle products, check if all required options are selected and bundle is in stock
    if (isBundle) {
      if (!product.items) return false;

      // First check if the bundle is in stock (using manual calculation)
      if (effectiveStockStatus !== 'IN_STOCK') {
        return false;
      }

      const allRequiredSelected = product.items.every(item => {
        if (!item.required) return true; // Optional items don't need to be selected

        // Check if any option is selected for this required item
        const hasSelection = item.options?.some(option =>
          selectedOptions[`${item.option_id}_${option.id}`]
        );
        return hasSelection;
      });

      return allRequiredSelected;
    }

    // For configurable products, check if all required options are selected
    const allOptionsSelected = product.configurable_options?.every(option =>
      selectedOptions[option.attribute_code] !== undefined
    );

    if (!allOptionsSelected) {
      return false;
    }

    // If variant is selected, check its stock status
    if (selectedVariant) {
      return selectedVariant.product.stock_status === 'IN_STOCK';
    }

    return false;
  };

  const getButtonText = () => {
    // For simple products
    if (!isConfigurable && !isGrouped && !isBundle) {
      return effectiveStockStatus === 'IN_STOCK' ? 'Add to Cart' : 'Out of Stock';
    }

    // For grouped products
    if (isGrouped) {
      const hasSelectedProducts = selectedOptions && Object.values(selectedOptions).some(qty => qty > 0);
      return hasSelectedProducts ? 'Add to Cart' : 'Please select products';
    }

    // For bundle products
    if (isBundle) {
      if (!product.items) return 'Bundle not available';

      // Check bundle stock status first (using manual calculation)
      if (effectiveStockStatus !== 'IN_STOCK') {
        return 'Out of Stock';
      }

      const allRequiredSelected = product.items.every(item => {
        if (!item.required) return true;
        const hasSelection = item.options?.some(option =>
          selectedOptions[`${item.option_id}_${option.id}`]
        );
        return hasSelection;
      });

      return allRequiredSelected ? 'Add to Cart' : 'Please select options';
    }

    // For configurable products
    const allOptionsSelected = product.configurable_options?.every(option =>
      selectedOptions[option.attribute_code] !== undefined
    );

    if (!allOptionsSelected) {
      return 'Please select options';
    }

    if (selectedVariant) {
      return selectedVariant.product.stock_status === 'IN_STOCK' ? 'Add to Cart' : 'Out of Stock';
    }

    return 'Configuration not available';
  };

  const handleAddToCart = () => {
    if (!canAddToCart()) return;

    if (isConfigurable && selectedVariant) {
      addToCart(selectedVariant.product.sku, quantity);
    } else if (isGrouped && selectedOptions) {
      // For grouped products, add each selected product with its quantity
      Object.entries(selectedOptions).forEach(([sku, qty]) => {
        if (qty > 0) {
          addToCart(sku, qty);
        }
      });
    } else if (isBundle) {
      // For bundle products, format the selected options for GraphQL
      const bundleOptions = [];

      if (product.items) {
        product.items.forEach(item => {
          item.options?.forEach(option => {
            const optionKey = `${item.option_id}_${option.id}`;
            const isSelected = selectedOptions[optionKey];

            if (isSelected) {
              const qtyKey = `${optionKey}_qty`;
              const qty = selectedOptions[qtyKey] || option.quantity || 1;

              bundleOptions.push({
                id: parseInt(item.option_id),
                value: parseInt(option.id),
                quantity: parseFloat(qty)
              });
            }
          });
        });
      }

      addBundleToCart(product.sku, quantity, bundleOptions);
    } else {
      addToCart(product.sku, quantity);
    }
  };

  const handleQuantityChange = (value) => {
    const numValue = parseInt(value);
    if (numValue > 0 && numValue <= 9999) {
      setQuantity(numValue);
    }
  };

  const incrementQuantity = () => {
    if (quantity < 9999) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      setReviewMessage('Please log in to write a review.');
      setTimeout(() => {
        setShowReviewModal(false);
        openLoginModal();
      }, 2000);
      return;
    }

    try {
      // Build ratings array from metadata
      let ratingsArray = [];

      if (ratingMetadata.length > 0) {
        // For each rating type (Quality, Price, Value), find the corresponding value_id
        ratingMetadata.forEach(ratingType => {
          // Find the value that matches the user's rating (1-5 stars)
          const ratingValue = ratingType.values.find(v => parseInt(v.value) === reviewForm.rating);

          if (ratingValue) {
            ratingsArray.push({
              id: ratingType.id,
              value_id: ratingValue.value_id
            });
          }
        });
      }

      // If no metadata found, use a simple approach with base64 encoding
      if (ratingsArray.length === 0) {
        // Most Magento installations have rating ID 1, 2, 3 with value IDs from 1-15
        // Rating ID 1 (Quality) typically has values 1-5, ID 2 (Value) has 6-10, ID 3 (Price) has 11-15
        const ratingId = btoa("1"); // Base64 encode rating ID
        const valueId = btoa(reviewForm.rating.toString()); // Base64 encode the value

        ratingsArray.push({
          id: ratingId,
          value_id: valueId
        });
      }

      console.log('Submitting review with ratings:', ratingsArray, 'Original rating:', reviewForm.rating);

      await createReview({
        variables: {
          input: {
            sku: product.sku,
            nickname: reviewForm.nickname,
            summary: reviewForm.summary,
            text: reviewForm.text,
            ratings: ratingsArray
          }
        }
      });

      setReviewMessage('Thank you for your review! It will be visible after approval.');
      setReviewForm({
        nickname: '',
        rating: 5,
        summary: '',
        text: ''
      });

      // Refetch product to get updated reviews
      setTimeout(() => {
        refetch();
        setShowReviewModal(false);
        setReviewMessage('');
      }, 2000);

    } catch (error) {
      console.error('Error submitting review:', error);

      // Extract more user-friendly error message
      let errorMessage = 'Failed to submit review. Please try again.';

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error.networkError) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setReviewMessage(errorMessage);
    }
  };

  const handleReviewFormChange = (field, value) => {
    setReviewForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWriteReviewClick = () => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    setShowReviewModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleAddToWishlist = async () => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }

    try {
      const wishlistId = wishlistData?.customer?.wishlists?.[0]?.id;

      if (!wishlistId) {
        setWishlistMessage('Wishlist not found. Please try again.');
        setTimeout(() => setWishlistMessage(''), 3000);
        return;
      }

      await addProductToWishlist({
        variables: {
          wishlistId: wishlistId,
          wishlistItems: [{
            sku: product.sku,
            quantity: 1
          }]
        },
        refetchQueries: [{ query: GET_CUSTOMER_WISHLIST }]
      });

      setWishlistMessage('Product added to wishlist!');
      setTimeout(() => setWishlistMessage(''), 3000);
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      setWishlistMessage(error.message || 'Failed to add to wishlist');
      setTimeout(() => setWishlistMessage(''), 5000);
    }
  };

  return (
    <div className="product-detail">
      <div className="product-detail-content">
        <div>
          <img
            src={product.media_gallery?.[0]?.url || '/placeholder.jpg'}
            alt={product.name}
            className="product-detail-image"
          />
        </div>

        <div className="product-detail-info">
          <h1>{product.name}</h1>

          {reviewCount > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div className="product-rating">
                <div className="product-stars" style={{ fontSize: '1.25rem' }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={star <= Math.round(ratingSummary / 20) ? 'star-filled' : 'star-empty'}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <span className="review-count" style={{ fontSize: '0.95rem' }}>
                  {(ratingSummary / 20).toFixed(1)} out of 5 ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            </div>
          )}

          <div className="product-detail-price">
            {discount?.amount_off > 0 && (
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '10px' }}>
                ${regularPrice?.value}
              </span>
            )}
            ${price?.value}
            {discount?.percent_off > 0 && (
              <span style={{ color: '#e53e3e', marginLeft: '10px' }}>
                -{discount.percent_off}%
              </span>
            )}
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <strong>SKU:</strong> {product.sku}
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <strong>Stock Status:</strong>{' '}
            <span className={formatStockStatus(effectiveStockStatus).className}>
              {formatStockStatus(effectiveStockStatus).text}
            </span>
            {product.only_x_left_in_stock && (
              <span style={{ color: '#e53e3e', marginLeft: '10px' }}>
                Only {product.only_x_left_in_stock} left!
              </span>
            )}
          </div>

          {/* Configurable Options */}
          {isConfigurable && product.configurable_options && (
            <div style={{ marginBottom: '1.5rem' }}>
              {product.configurable_options.map((option) => (
                <div key={option.id} style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    fontSize: '0.875rem'
                  }}>
                    {option.label}:
                  </label>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    {option.values.map((value) => {
                      const isSelected = selectedOptions[option.attribute_code] === value.value_index;
                      return (
                        <button
                          key={value.value_index}
                          onClick={() => handleOptionChange(option.attribute_code, value.value_index)}
                          style={{
                            padding: '0.5rem 1rem',
                            border: `2px solid ${isSelected ? '#7c3aed' : '#d1d5db'}`,
                            borderRadius: '6px',
                            backgroundColor: isSelected ? '#7c3aed' : 'white',
                            color: isSelected ? 'white' : '#374151',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            transition: 'all 0.2s',
                            minWidth: '60px'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.target.style.borderColor = '#7c3aed';
                              e.target.style.backgroundColor = '#f3f4f6';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.target.style.borderColor = '#d1d5db';
                              e.target.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          {value.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Grouped Product Options */}
          {isGrouped && product.items && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '1rem',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}>
                Select Products:
              </label>
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {product.items.map((item, index) => {
                  const groupedProduct = item.product;
                  const selectedQty = selectedOptions[groupedProduct.sku] || 0;

                  return (
                    <div
                      key={groupedProduct.sku}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '1rem',
                        borderBottom: index < product.items.length - 1 ? '1px solid #e5e7eb' : 'none',
                        gap: '1rem'
                      }}
                    >
                      {groupedProduct.small_image?.url && (
                        <img
                          src={groupedProduct.small_image.url}
                          alt={groupedProduct.name}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '6px'
                          }}
                        />
                      )}

                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          margin: '0 0 0.25rem 0',
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#1f2937'
                        }}>
                          {groupedProduct.name}
                        </h4>
                        <div style={{
                          fontSize: '0.875rem',
                          color: '#6b7280',
                          marginBottom: '0.25rem'
                        }}>
                          SKU: {groupedProduct.sku}
                        </div>
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#1f2937'
                        }}>
                          ${groupedProduct.price_range?.minimum_price?.final_price?.value || 'N/A'}
                        </div>
                        <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                          <span className={formatStockStatus(groupedProduct.stock_status).className}>
                            {formatStockStatus(groupedProduct.stock_status).text}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button
                          onClick={() => {
                            const newQty = Math.max(0, selectedQty - 1);
                            setSelectedOptions(prev => ({
                              ...prev,
                              [groupedProduct.sku]: newQty
                            }));
                          }}
                          disabled={selectedQty <= 0}
                          style={{
                            width: '32px',
                            height: '32px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            color: '#374151',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: selectedQty > 0 ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          -
                        </button>

                        <input
                          type="number"
                          min="0"
                          max="999"
                          value={selectedQty}
                          onChange={(e) => {
                            const newQty = Math.max(0, parseInt(e.target.value) || 0);
                            setSelectedOptions(prev => ({
                              ...prev,
                              [groupedProduct.sku]: newQty
                            }));
                          }}
                          style={{
                            width: '60px',
                            height: '32px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            textAlign: 'center',
                            fontSize: '0.875rem'
                          }}
                        />

                        <button
                          onClick={() => {
                            const newQty = selectedQty + 1;
                            setSelectedOptions(prev => ({
                              ...prev,
                              [groupedProduct.sku]: newQty
                            }));
                          }}
                          style={{
                            width: '32px',
                            height: '32px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            color: '#374151',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bundle Product Options */}
          {isBundle && product.items && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '1rem',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}>
                Customize Your Bundle:
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {product.items.map((item) => (
                  <div
                    key={item.option_id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '1rem'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                      <h4 style={{
                        margin: 0,
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#1f2937'
                      }}>
                        {item.title}
                      </h4>
                      {item.required && (
                        <span style={{
                          marginLeft: '0.5rem',
                          fontSize: '0.75rem',
                          color: '#dc2626',
                          fontWeight: '500'
                        }}>
                          *Required
                        </span>
                      )}
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem'
                    }}>
                      {item.options?.map((option) => {
                        const optionKey = `${item.option_id}_${option.id}`;
                        const isSelected = selectedOptions[optionKey];
                        const selectedQty = selectedOptions[`${optionKey}_qty`] || option.quantity || 1;

                        return (
                          <div
                            key={option.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '0.75rem',
                              border: `1px solid ${isSelected ? '#7c3aed' : '#e5e7eb'}`,
                              borderRadius: '6px',
                              backgroundColor: isSelected ? '#f3f4f6' : 'white',
                              gap: '1rem'
                            }}
                          >
                            <input
                              type={item.type === 'multi' ? 'checkbox' : 'radio'}
                              name={`bundle_${item.option_id}`}
                              checked={isSelected || false}
                              onChange={(e) => {
                                if (item.type === 'multi') {
                                  setSelectedOptions(prev => ({
                                    ...prev,
                                    [optionKey]: e.target.checked
                                  }));
                                } else {
                                  // For radio buttons, clear other options in the same group
                                  setSelectedOptions(prev => {
                                    const newOptions = { ...prev };
                                    // Clear other options in this group
                                    item.options?.forEach(opt => {
                                      if (opt.id !== option.id) {
                                        delete newOptions[`${item.option_id}_${opt.id}`];
                                        delete newOptions[`${item.option_id}_${opt.id}_qty`];
                                      }
                                    });
                                    // Set this option
                                    newOptions[optionKey] = e.target.checked;
                                    return newOptions;
                                  });
                                }
                              }}
                              style={{
                                width: '16px',
                                height: '16px'
                              }}
                            />

                            {option.product?.small_image?.url && (
                              <img
                                src={option.product.small_image.url}
                                alt={option.product.name}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  objectFit: 'cover',
                                  borderRadius: '4px'
                                }}
                              />
                            )}

                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#1f2937'
                              }}>
                                {option.product?.name || `Option ${option.id}`}
                              </div>
                              <div style={{
                                fontSize: '0.75rem',
                                color: '#6b7280'
                              }}>
                                {option.product?.sku && `SKU: ${option.product.sku}`}
                              </div>
                              {option.product?.stock_status && (
                                <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                  <span className={formatStockStatus(option.product.stock_status).className}>
                                    {formatStockStatus(option.product.stock_status).text}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem'
                            }}>
                              <div style={{
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                color: '#1f2937'
                              }}>
                                ${option.product?.price_range?.minimum_price?.final_price?.value || option.price || '0'}
                              </div>

                              {option.can_change_quantity && isSelected && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}>
                                  <button
                                    onClick={() => {
                                      const newQty = Math.max(1, selectedQty - 1);
                                      setSelectedOptions(prev => ({
                                        ...prev,
                                        [`${optionKey}_qty`]: newQty
                                      }));
                                    }}
                                    disabled={selectedQty <= 1}
                                    style={{
                                      width: '24px',
                                      height: '24px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      backgroundColor: 'white',
                                      color: '#374151',
                                      fontSize: '0.75rem',
                                      cursor: selectedQty > 1 ? 'pointer' : 'not-allowed',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    -
                                  </button>

                                  <span style={{
                                    fontSize: '0.75rem',
                                    minWidth: '20px',
                                    textAlign: 'center'
                                  }}>
                                    {selectedQty}
                                  </span>

                                  <button
                                    onClick={() => {
                                      const newQty = selectedQty + 1;
                                      setSelectedOptions(prev => ({
                                        ...prev,
                                        [`${optionKey}_qty`]: newQty
                                      }));
                                    }}
                                    style={{
                                      width: '24px',
                                      height: '24px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      backgroundColor: 'white',
                                      color: '#374151',
                                      fontSize: '0.75rem',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    +
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {wishlistMessage && (
            <div style={{
              padding: '0.75rem',
              marginBottom: '1rem',
              backgroundColor: wishlistMessage.includes('Failed') ? '#fee2e2' : '#d1fae5',
              color: wishlistMessage.includes('Failed') ? '#991b1b' : '#065f46',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}>
              {wishlistMessage}
            </div>
          )}

          {/* Quantity Input */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}>
              Quantity:
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={decrementQuantity}
                disabled={quantity <= 1}
                style={{
                  width: '40px',
                  height: '40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  cursor: quantity > 1 ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (quantity > 1) {
                    e.target.style.borderColor = '#7c3aed';
                    e.target.style.color = '#7c3aed';
                  }
                }}
                onMouseLeave={(e) => {
                  if (quantity > 1) {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.color = '#374151';
                  }
                }}
              >
                -
              </button>
              <input
                type="number"
                min="1"
                max="9999"
                value={quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                className="qty-input"
                style={{
                  width: '80px',
                  height: '40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  textAlign: 'center',
                  fontSize: '1rem',
                  fontWeight: '500',
                  padding: '0'
                }}
              />
              <button
                onClick={incrementQuantity}
                disabled={quantity >= 9999}
                style={{
                  width: '40px',
                  height: '40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  cursor: quantity < 9999 ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (quantity < 9999) {
                    e.target.style.borderColor = '#7c3aed';
                    e.target.style.color = '#7c3aed';
                  }
                }}
                onMouseLeave={(e) => {
                  if (quantity < 9999) {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.color = '#374151';
                  }
                }}
              >
                +
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <button
              onClick={handleAddToCart}
              disabled={cartLoading || !canAddToCart()}
              style={{
                flex: 1,
                padding: '0.75rem 2rem',
                backgroundColor: canAddToCart() ? '#7c3aed' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: canAddToCart() ? 'pointer' : 'not-allowed',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                if (canAddToCart()) {
                  e.target.style.backgroundColor = '#6d28d9';
                }
              }}
              onMouseLeave={(e) => {
                if (canAddToCart()) {
                  e.target.style.backgroundColor = '#7c3aed';
                }
              }}
            >
              {cartLoading ? 'Adding...' : getButtonText()}
            </button>

            <button
              onClick={handleAddToWishlist}
              disabled={wishlistLoading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'white',
                color: '#7c3aed',
                border: '2px solid #7c3aed',
                borderRadius: '6px',
                fontSize: '1.5rem',
                fontWeight: '600',
                cursor: wishlistLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '60px',
                lineHeight: '1'
              }}
              onMouseEnter={(e) => {
                if (!wishlistLoading) {
                  e.target.style.backgroundColor = '#7c3aed';
                  e.target.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (!wishlistLoading) {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = '#7c3aed';
                }
              }}
              title="Add to Wishlist"
            >
              {wishlistLoading ? '...' : '♥'}
            </button>
          </div>
        </div>
      </div>

      {/* Product Tabs */}
      <div className="product-tabs">
        <div className="tab-headers">
          <button
            className={`tab-header ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Product Details
          </button>
          <button
            className={`tab-header ${activeTab === 'more-info' ? 'active' : ''}`}
            onClick={() => setActiveTab('more-info')}
          >
            More Information
          </button>
          <button
            className={`tab-header ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            Reviews
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'details' && (
            <div className="tab-panel">
              {product.description?.html ? (
                <div dangerouslySetInnerHTML={{ __html: product.description.html }} />
              ) : (
                <p>No product details available.</p>
              )}
            </div>
          )}

          {activeTab === 'more-info' && (
            <div className="tab-panel">
              <table className="product-attributes">
                <tbody>
                  <tr>
                    <th>SKU</th>
                    <td>{product.sku}</td>
                  </tr>
                  <tr>
                    <th>Stock Status</th>
                    <td>
                      <span className={formatStockStatus(effectiveStockStatus).className}>
                        {formatStockStatus(effectiveStockStatus).text}
                      </span>
                    </td>
                  </tr>
                  {product.only_x_left_in_stock && (
                    <tr>
                      <th>Available Quantity</th>
                      <td>{product.only_x_left_in_stock}</td>
                    </tr>
                  )}
                  {isConfigurable && product.configurable_options && (
                    product.configurable_options.map((option) => (
                      <tr key={option.id}>
                        <th>{option.label}</th>
                        <td>{option.values.map(v => v.label).join(', ')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="tab-panel">
              <div className="reviews-section">
                <div className="reviews-header">
                  <div>
                    <h3>Customer Reviews</h3>
                    {reviewCount > 0 && (
                      <div className="rating-summary">
                        <div className="stars-display">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              className={star <= Math.round(ratingSummary / 20) ? 'star-filled' : 'star-empty'}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                        <span className="rating-text">
                          {(ratingSummary / 20).toFixed(1)} out of 5 ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleWriteReviewClick}
                    className="write-review-btn"
                  >
                    Write a Review
                  </button>
                </div>

                {reviewCount === 0 ? (
                  <p className="no-reviews">No reviews yet. Be the first to review this product!</p>
                ) : (
                  <div className="reviews-list">
                    {reviews.map((review, index) => (
                      <div key={index} className="review-item">
                        <div className="review-header-item">
                          <div>
                            <div className="review-stars">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                  key={star}
                                  className={star <= review.average_rating ? 'star-filled' : 'star-empty'}
                                >
                                  ★
                                </span>
                              ))}
                            </div>
                            <p className="review-author">
                              {review.nickname} • {formatDate(review.created_at)}
                            </p>
                          </div>
                        </div>
                        <h4 className="review-summary">{review.summary}</h4>
                        <p className="review-text">{review.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="review-modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="review-modal-header">
              <h2>Write a Review</h2>
              <button
                className="close-btn"
                onClick={() => setShowReviewModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleReviewSubmit} className="review-form">
              {reviewMessage && (
                <div className={`review-message ${reviewMessage.includes('Failed') || reviewMessage.includes('log in') ? 'error' : 'success'}`}>
                  {reviewMessage}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="nickname">Nickname *</label>
                <input
                  type="text"
                  id="nickname"
                  value={reviewForm.nickname}
                  onChange={(e) => handleReviewFormChange('nickname', e.target.value)}
                  required
                  placeholder="Enter your nickname"
                  disabled={reviewLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="rating">Rating *</label>
                <div className="rating-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`star ${star <= reviewForm.rating ? 'filled' : ''}`}
                      onClick={() => handleReviewFormChange('rating', star)}
                      disabled={reviewLoading}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="summary">Summary *</label>
                <input
                  type="text"
                  id="summary"
                  value={reviewForm.summary}
                  onChange={(e) => handleReviewFormChange('summary', e.target.value)}
                  required
                  placeholder="Brief summary of your review"
                  disabled={reviewLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="text">Review *</label>
                <textarea
                  id="text"
                  value={reviewForm.text}
                  onChange={(e) => handleReviewFormChange('text', e.target.value)}
                  required
                  rows="5"
                  placeholder="Write your detailed review here..."
                  disabled={reviewLoading}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowReviewModal(false)}
                  disabled={reviewLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={reviewLoading}
                >
                  {reviewLoading ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Product;